/**
 * Per-workspace spend chart.
 *
 * Aggregates the recent-transactions window by ``reference.workspace_id``
 * and renders one horizontal bar per workspace, stacked by spend
 * category (same palette as CategoryDonut). Debits that carry no
 * workspace attribution (account-level operations, or rows written
 * before workspace stamping existed) are grouped under "Unattributed"
 * so the chart total reconciles with the spend chart.
 */

import React, { useMemo } from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import { useTheme } from '@mui/material/styles';

import {
  describeActivity, isSpendCategory, CATEGORY_COLORS, SPEND_CATEGORIES,
} from './billingHelpers';

const MAX_BARS = 8;
const UNATTRIBUTED = 'Unattributed';

const WorkspaceSpendChart = ({ transactions = [], workspaces = [], height }) => {
  const theme = useTheme();

  const { rows, keys, total } = useMemo(() => {
    const nameByUid = {};
    (workspaces || []).forEach((w) => { nameByUid[w.uid] = w.name || w.uid; });

    // totals[workspaceLabel] = { total, perCategory: {cat: credits} }
    const totals = {};
    (transactions || []).forEach((txn) => {
      if (!txn?.amount || txn.amount >= 0) return;  // debits only
      const { category } = describeActivity(txn.reason);
      if (!isSpendCategory(category)) return;
      const wsId = txn.reference?.workspace_id;
      const label = wsId ? (nameByUid[wsId] || wsId) : UNATTRIBUTED;
      const bucket = totals[label] || (totals[label] = { total: 0, perCategory: {} });
      bucket.total += -txn.amount;
      bucket.perCategory[category] = (bucket.perCategory[category] || 0) + (-txn.amount);
    });

    let entries = Object.entries(totals).sort(([, a], [, b]) => b.total - a.total);

    // Collapse the long tail so the chart stays readable. Unattributed
    // keeps its own bar (it's a meaningful bucket, not a workspace).
    if (entries.length > MAX_BARS) {
      const head = entries.slice(0, MAX_BARS - 1);
      const tail = entries.slice(MAX_BARS - 1);
      const merged = { total: 0, perCategory: {} };
      tail.forEach(([, b]) => {
        merged.total += b.total;
        Object.entries(b.perCategory).forEach(([cat, v]) => {
          merged.perCategory[cat] = (merged.perCategory[cat] || 0) + v;
        });
      });
      entries = [...head, [`Other (${tail.length} workspaces)`, merged]];
    }

    const usedCategories = SPEND_CATEGORIES.filter(
      (cat) => entries.some(([, b]) => (b.perCategory[cat] || 0) > 0),
    );

    // nivo bar rows; reversed so the biggest spender renders on top
    // (nivo draws horizontal rows bottom-up).
    const barRows = entries.slice().reverse().map(([label, b]) => {
      const row = { workspace: label };
      usedCategories.forEach((cat) => {
        row[cat] = Number((b.perCategory[cat] || 0).toFixed(2));
      });
      return row;
    });

    return {
      rows: barRows,
      keys: usedCategories,
      total: entries.reduce((s, [, b]) => s + b.total, 0),
    };
  }, [transactions, workspaces]);

  const chartHeight = height || Math.max(180, rows.length * 44 + 50);

  if (!rows.length || total === 0) {
    return (
      <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No workspace spend yet
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ height: chartHeight }}>
        <ResponsiveBar
          data={rows}
          keys={keys}
          indexBy="workspace"
          layout="horizontal"
          margin={{ top: 4, right: 24, bottom: 36, left: 140 }}
          padding={0.35}
          colors={(d) => CATEGORY_COLORS[d.id] || theme.palette.primary.main}
          borderRadius={2}
          enableLabel={false}
          enableGridY={false}
          enableGridX
          axisBottom={{
            tickSize: 0,
            tickPadding: 6,
            tickValues: 5,
            format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            // Workspace names can be long; truncate so the axis
            // doesn't swallow the plot area.
            format: (v) => (String(v).length > 18 ? `${String(v).slice(0, 17)}…` : v),
          }}
          theme={{
            axis: {
              ticks: { text: { fill: theme.palette.text.secondary, fontSize: 11 } },
            },
            grid: { line: { stroke: theme.palette.divider, strokeWidth: 1 } },
          }}
          tooltip={({ id, value, indexValue, color }) => (
            <Box sx={{
              bgcolor: 'background.paper',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1, px: 1.5, py: 1,
              boxShadow: theme.shadows[4],
            }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                {indexValue}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color }}>
                {id}: {Number(value).toLocaleString()} credits
              </Typography>
            </Box>
          )}
        />
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={1.5} justifyContent="center" sx={{ mt: 1 }}>
        {keys.map((cat) => (
          <Stack key={cat} direction="row" alignItems="center" spacing={0.5}>
            <Box sx={{
              width: 8, height: 8, borderRadius: '50%',
              bgcolor: CATEGORY_COLORS[cat] || theme.palette.primary.main,
            }} />
            <Typography variant="caption" color="text.secondary">{cat}</Typography>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default WorkspaceSpendChart;
